/**
 * Routing / isochrone provider contract (M8-B) — the vendor-abstracted geo-core
 * seam the deep-analysis layer calls for (a) drive-time isochrone polygons and
 * (b) an optimized stop order + distance/time matrix. Mirrors the tile /
 * geocoder abstraction posture: the analysis layer binds to THIS interface,
 * never to a vendor or URL, and every provider carries a NON-REMOVABLE
 * `attribution` string that rides through to the map like the tile attribution.
 *
 * A deployment swaps the concrete provider (self-hosted Valhalla/OSRM, or a
 * metered vendor as fallback) in one place; nothing downstream changes. The
 * OSS-first default (`haversineRoutingProvider`) computes everything client-side
 * with no external call, so route optimization is real and verifiable now and
 * drive-time degrades to a clearly-labeled straight-line approximation until a
 * real routing engine is configured.
 */

import type { LngLat } from "./geo";

export type { LngLat } from "./geo";

/** A stop to visit: a stable id (row id) + its coordinate. */
export interface RouteStop {
  id: string;
  coord: LngLat;
}

/** A full O/D matrix over an ordered id list — distances (m) and durations (s). */
export interface RouteMatrix {
  /** Row/column order: `distances[i][j]` is ids[i] → ids[j]. */
  ids: string[];
  /** Great-circle (or vendor road) distance in meters. */
  distances: number[][];
  /** Travel duration in seconds. */
  durations: number[][];
}

/** The optimized route: stop ids in visiting order + the matrix + leg totals. */
export interface OptimizeResult {
  /** Stop ids in the optimized visiting order (an open path, first → last). */
  order: string[];
  matrix: RouteMatrix;
  /** Summed distance (m) and duration (s) over the consecutive legs. */
  totals: { distance: number; duration: number };
}

/** One requested isochrone band — a time (minutes) OR distance (miles) budget. */
export interface IsochroneBand {
  minutes?: number;
  miles?: number;
}

/** A returned isochrone: the requested band + its reachable-area polygon ring. */
export interface Isochrone {
  band: IsochroneBand;
  /** Closed [lng,lat] ring bounding the reachable area. */
  polygon: LngLat[];
  /**
   * TRUE when the ring is a straight-line (radius-buffer) approximation rather
   * than a real road-network isochrone — surfaced in the UI so an operator
   * knows a routing engine is not yet configured. The OSS default sets this;
   * a real Valhalla/OSRM adapter sets it false.
   */
  approximate: boolean;
}

/**
 * The geo-core routing/isochrone provider. Both calls are async because a real
 * provider fetches; the OSS default resolves synchronously-computed results.
 * Route every METERED provider call through the M7 entitlement gate before
 * invoking `getIsochrones` / `optimizeRoute` (the caller's responsibility).
 */
export interface RoutingProvider {
  /** Non-removable attribution for the routing/isochrone source (like tiles). */
  readonly attribution: string;
  /** Isochrone polygons for `origin` at each requested band. */
  getIsochrones(origin: LngLat, bands: IsochroneBand[]): Promise<Isochrone[]>;
  /** Optimized visiting order + matrix + totals for a stop set. */
  optimizeRoute(stops: RouteStop[]): Promise<OptimizeResult>;
}

/** Route-optimization stop cap (light, non-fleet use case — M8 R4). */
export const MAX_ROUTE_STOPS = 70;

/** Assumed average travel speed for the OSS estimate (km/h) — operator-tunable. */
export const DEFAULT_ROUTE_SPEED_KMH = 50;

/** Thrown when a stop set exceeds {@link MAX_ROUTE_STOPS} (fleet-grade is out of scope). */
export class RouteStopLimitError extends Error {
  constructor(
    readonly requested: number,
    readonly limit: number = MAX_ROUTE_STOPS,
  ) {
    super(`Route optimization is capped at ${limit} stops (got ${requested}).`);
    this.name = "RouteStopLimitError";
  }
}
